/**
@format
@flow
*/
import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import AnimationConfig from './config';

type Props = $ReadOnly<{|
  /**
   * Determines the type of animation to be used.
   */
   animation?: 'fadeInUp' | 'fadeInDown' | 'fadeDownOut' | 'fadeInLeft' | 'fadeOutRight' | 'slideUp' | 'slideDown',
   /**
   * If set to true, children wrapped in component will be animated.
   */
   animated: boolean,
   /**
    * To signal to start animation. Could be used as a delay or auto
    */
   startAnimation?: boolean,
   /**
    * Reset animation
    */
   autoReset?: boolean,
   /**
   * Children to be passed into animation wrapper.
   */
   children: React$Node,
   /**
    * Custom function to configure when animation ends
    */
   onAnimationEnd?: (event?: SyntheticAnimationEvent<HTMLDivElement>) => void,
    /**
    * Custom function to configure when animation starts
    */
   onAnimationStart?: (event?: SyntheticAnimationEvent<HTMLDivElement>) => void,
 |}>;

const buildAnimation = (animation) => {
  if (animation === {} || !animation) {
    return null;
  }

  const {
    duration, delay, timingFunction, keyFrame,
  } = animation;
  const animationKeyframe = keyframes`${keyFrame}`;

  return css`
    animation: ${duration} ${timingFunction} ${delay} ${animationKeyframe};
  `;
};

const AnimatorWrapper = styled.div`
  position: relative;
  ${({ animation, animated }) => animated && buildAnimation(animation)};
  animation-play-state: ${({ startAnimation }) => (startAnimation ? 'running' : 'paused')};
  animation-fill-mode: forwards;
`;

const AnimationWrapper = (props: Props): React$Node => {
  const {
    animation: animationType, animated, children, startAnimation, autoReset, ...rest
  } = props;
  const animation = AnimationConfig[animationType];

  const [animationStarted, setAnimationStarted] = useState(false);
  const [animationFinished, setAnimationFinished] = useState(false);

  const onAnimationStart = (): void => {
    setAnimationStarted(true);
    setAnimationFinished(false);
  };

  const onAnimationEnd = (): void => {
    setAnimationFinished(true);
  };

  /**
   * Status of animation: running or paused
  */
  const status = (startAnimation && !animationFinished)
  || (!startAnimation && !animationFinished && animationStarted);
  // Line 84: allows for status to be true, or act as a reset when startAnimation
  // changes from true to false, as passed in so that the right animation is played

  /**
   * Function to determine the animation type if autoReset is set to true
   * Upon entering page, animation will be ''
   * This prevents animation to be played initially
   */
  const getAnimation = (): Object => {
    if (!animationStarted && !animationFinished && !status) {
      return undefined;
    }
    return animation;
  };

  return (
    <AnimatorWrapper
      animated={animated}
      animation={autoReset ? getAnimation() : animation}
      {...(autoReset
        ? {
          onAnimationStart,
          onAnimationEnd,
          startAnimation: status,
        }
        : { startAnimation }
      )}
      {...rest}
    >
      {children}
    </AnimatorWrapper>
  );
};

export default AnimationWrapper;
